import { useObservableState } from 'observable-hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';
import Form, { SubmitType } from '../../../components/form';
import Button from '../../../components/form/elements/button';
import File from '../../../components/form/elements/file';
import Group from '../../../components/form/elements/group';
import Link from '../../../components/form/elements/link';
import Readonly from '../../../components/form/elements/readonly';
import Text from '../../../components/form/elements/text';
import SystemConfiguration from '../../../interfaces/systemConfiguration';
import { INTERNAL_SYSTEM_PROPERTIES_INDEX } from '../../../router/routes';
import { useService } from '../../../services/services';
import { map } from 'rxjs/operators';
import { when } from '../../../services/utils';

export default function EditSystemConfiguration() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { systemPropertyService } = useService();
  const [schema, setSchema] = useState(null);
  const [bodyElements, setBodyElements] = useState([]);
  const [footerElements, setFooterElements] = useState([]);

  const [systemProperty] = useObservableState<SystemConfiguration>(() =>
    systemPropertyService.edit(id).pipe(
      map((property) => {
        if (property.type === 'file') {
          delete property.value;
        } else {
          delete property.files;
        }

        return property;
      })
    )
  );

  // Set schema for value or files field
  useEffect(() => {
    if (!systemProperty || schema) {
      return;
    }

    setSchema(
      yup.object().shape({
        name: yup.string().max(255).required(),
        description: yup.string().max(255).required(),
        files: when(
          () => systemProperty.type === 'file',
          yup
            .array()
            .of(
              yup.object().shape({
                id: yup.string().required().uuid(),
              })
            )
            .min(1),
          yup.object().nullable()
        ),
        value: when(
          () => systemProperty.type !== 'file',
          yup.string().max(255).required(),
          yup.string().max(255).nullable()
        ),
      })
    );
  }, [systemProperty, schema]);

  useEffect(() => {
    if (systemProperty) {
      setBodyElements([
        Group([
          Readonly([Text('name'), Text('description')]),
          systemProperty.type === 'file'
            ? File('files', 'file', false)
            : Text('value'),
        ]),
      ]);
      setFooterElements([
        Button('save'),
        Link(INTERNAL_SYSTEM_PROPERTIES_INDEX, 'cancel'),
      ]);
    }
  }, [id, systemProperty, setBodyElements, setFooterElements]);

  const submit = useCallback<SubmitType<SystemConfiguration>>(
    (form) => {
      return [
        systemPropertyService.update(id, form),
        () => history.push(INTERNAL_SYSTEM_PROPERTIES_INDEX),
      ];
    },
    [id, systemPropertyService, history]
  );

  return (
    <Form
      title={
        systemProperty
          ? systemProperty.name
          : t('pages.internal.system-configuration.Edit system configuration')
      }
      backTo={INTERNAL_SYSTEM_PROPERTIES_INDEX}
      mode={'edit'}
      body={bodyElements}
      footer={footerElements}
      values={systemProperty}
      submit={submit}
      schema={schema}
    />
  );
}
